/**
 * Variant rules. `belgian` is the real target — Flemish *manillen*, as
 * documented in docs/rules-belgian.md. `loose` keeps the absolute follow-suit
 * rule but drops the overtake obligations and the betting, for a first game
 * with people who have never played.
 */

export type VariantId = 'belgian' | 'loose';

export interface VariantRules {
  readonly id: VariantId;
  readonly label: string;
  /** Following suit is absolute in every real Manille, so it is not a flag. */
  /** Holding the led suit and able to beat the master in it → you must. */
  readonly mustOvertakeInSuit: boolean;
  /** Trump was led and you hold trump: you must beat the master if you can. */
  readonly mustOvertrumpWhenTrumpLed: boolean;
  /** Void in the led suit and holding trump: you must trump. */
  readonly mustTrumpWhenVoid: boolean;
  /** Void, an opponent already trumped, you can beat it: you must. */
  readonly mustOvertrumpWhenVoid: boolean;
  /** Void, an opponent already trumped, you cannot beat it: must you still trump? */
  readonly mustTrumpEvenIfLosing: boolean;
  /** Your partner is master → every obligation above is lifted (follow-suit stays). */
  readonly partnerMasterExempts: boolean;
  /** The dealer may declare *zonder troef*, which doubles the deal. */
  readonly allowNoTrump: boolean;
  /** *Kloppen* / *tegenkloppen* — the ×2 / ×4 raise after the declaration. */
  readonly allowDoubling: boolean;
  /** A capot (all eight tricks) doubles the margin: 30 → 60. */
  readonly capotDoubles: boolean;
  /** A *plat* (30–30) scores nothing and doubles the next deal instead. */
  readonly platCarriesDouble: boolean;
  /** Match points needed to win. 101 is the near-universal Belgian "boom". */
  readonly target: number;
}

export const MATCH_TARGETS = [51, 61, 101, 121, 151] as const;

export const VARIANTS: Record<VariantId, VariantRules> = {
  belgian: {
    id: 'belgian',
    label: 'Belgian',
    mustOvertakeInSuit: true,
    mustOvertrumpWhenTrumpLed: true,
    mustTrumpWhenVoid: true,
    mustOvertrumpWhenVoid: true,
    mustTrumpEvenIfLosing: true,
    partnerMasterExempts: true,
    allowNoTrump: true,
    allowDoubling: true,
    capotDoubles: true,
    platCarriesDouble: true,
    target: 101,
  },
  loose: {
    id: 'loose',
    label: 'Relaxed',
    mustOvertakeInSuit: false,
    mustOvertrumpWhenTrumpLed: false,
    mustTrumpWhenVoid: true,
    mustOvertrumpWhenVoid: false,
    mustTrumpEvenIfLosing: false,
    partnerMasterExempts: true,
    allowNoTrump: false,
    allowDoubling: false,
    capotDoubles: true,
    platCarriesDouble: false,
    target: 101,
  },
};

export const DEFAULT_VARIANT: VariantId = 'belgian';

export function resolveVariant(variant: VariantId | VariantRules): VariantRules {
  return typeof variant === 'string' ? VARIANTS[variant] : variant;
}
